import { useState, useEffect, useRef, Fragment } from "react";
import {
  Autocomplete,
  FormControl,
  TextField,
  Grid,
  Card,
  CardContent,
  TextFielDecimalNumbers,
  Switch,
  CircularProgress,
} from "../../../../components";
import { InputErros, value } from "../../../../helpers";
import {
  BancosCaixasService,
  PlanosFinanceirosService,
} from "../../../../services";
import { responseErros } from "../../../../utils";
import "../../configuracoes.style.css";

const ConfiguracaoFinanceiroTab = ({
  configuracao,
  onBlur,
  onChange,
  onChangeAutocomplete,
  onChangeSwitch,
}) => {
  const bancosCaixasService = new BancosCaixasService();
  const planosFinanceiroService = new PlanosFinanceirosService();
  const [inputErro, setInputErro] = useState([]);
  const [listaContas, setListaContas] = useState([]);
  const [
    inputPlanoFinanceiroDescontoNfSaidaRef,
    inputPlanoFinanceiroJurosRecebidosRef,
    inputPlanoFinanceiroMultaRecebidaRef,
  ] = [useRef(), useRef(), useRef()];
  const [listaPlanosFinanceirosDesconto, setListaPlanosFinanceirosDesconto] =
    useState([]);
  const [listaPlanosFinanceirosJuros, setListaPlanosFinanceirosJuros] =
    useState([]);
  const [listaPlanosFinanceirosMulta, setListaPlanosFinanceirosMulta] =
    useState([]);
  const [loadingDesconto, setLoadingDesconto] = useState(false);
  const [loadingJuros, setLoadingJuros] = useState(false);
  const [loadingMulta, setLoadingMulta] = useState(false);
  const inputErros = new InputErros(inputErro, setInputErro);

  useEffect(() => {
    buscarContas();
    const ids = [
      configuracao?.planoFinanceiroDescontoNfSaidaId,
      configuracao?.planoFinanceiroJurosRecebidosId,
      configuracao?.planoFinanceiroMultaRecebidaId,
    ].filter(Boolean);
    if (ids.length) {
      buscarPlanosFinanceirosIniciais(ids);
    }
  }, []);

  const buscarContas = async function () {
    const filtros = {
      nonPaginated: true,
    };
    const result = await bancosCaixasService.getAll(filtros);
    if (!result.isAxiosError) {
      setListaContas(result.data);
    } else {
      responseErros(result);
    }
  };

  const buscarPlanosFinanceirosIniciais = async (ids) => {
    const res = await planosFinanceiroService.getAll({ id: ids });
    if (!res.isAxiosError) {
      const rows = res.data.rows;
      setListaPlanosFinanceirosDesconto(
        rows.filter(
          (p) => p.id === configuracao?.planoFinanceiroDescontoNfSaidaId,
        ),
      );
      setListaPlanosFinanceirosJuros(
        rows.filter(
          (p) => p.id === configuracao?.planoFinanceiroJurosRecebidosId,
        ),
      );
      setListaPlanosFinanceirosMulta(
        rows.filter(
          (p) => p.id === configuracao?.planoFinanceiroMultaRecebidaId,
        ),
      );
    } else {
      responseErros(res);
    }
  };

  const buscarPlanosFinanceiros = async (filtros, setLista, setLoading) => {
    const res = await planosFinanceiroService.getAll(filtros);
    if (!res.isAxiosError) {
      setLista(res.data.rows);
    } else {
      responseErros(res);
    }
    setLoading(false);
  };

  const onSearchChangePlanoFinanceiro = (event, ref, setLista, setLoading) => {
    if (event.key === "Enter" && ref.current === document.activeElement) {
      const eventValue = event.target?.value;
      let filtros = {
        descricao: eventValue,
        ativado: true,
      };
      setLoading(true);
      buscarPlanosFinanceiros(filtros, setLista, setLoading);
    }
  };

  return (
    <Card className="p-2">
      <CardContent>
        <Grid container spacing={2} className={"config-card"}>
          <Grid item xs={9}>
            FIN001 - Conta/Caixa padrão do sistema.
          </Grid>
          <Grid item xs={3}>
            <FormControl variant="outlined" fullWidth>
              <Autocomplete
                id="bancoCaixaId"
                name="bancoCaixaId"
                noOptionsText="Sem opções"
                options={listaContas}
                autoHighlight
                disableClearable
                getOptionLabel={(option) =>
                  option?.nome ? `${option.id}` + ` - ` + `${option.nome}` : ""
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Banco/Caixa"
                    variant="outlined"
                    error={inputErros.get("bancoCaixaId")}
                  />
                )}
                onChange={(e, value) =>
                  onChangeAutocomplete("bancoCaixaId", value)
                }
                value={value.autoComplete(
                  listaContas,
                  configuracao?.bancoCaixaId,
                )}
              />
            </FormControl>
          </Grid>
        </Grid>
        <Grid container spacing={2} className={"config-card"}>
          <Grid item xs={9}>
            FIN002 - Percentual de juros.
          </Grid>
          <Grid item xs={3}>
            <TextFielDecimalNumbers
              id="jurosPorcentagem"
              name="jurosPorcentagem"
              label="Juros (%)"
              variant="outlined"
              value={configuracao?.jurosPorcentagem ?? 0}
              onBlur={(event) => onBlur(event)}
            />
          </Grid>
        </Grid>
        <Grid container spacing={2} className={"config-card"}>
          <Grid item xs={9}>
            FIN003 - Percentual de multa.
          </Grid>
          <Grid item xs={3}>
            <TextFielDecimalNumbers
              id="multaPorcentagem"
              name="multaPorcentagem"
              label="Multa (%)"
              variant="outlined"
              value={configuracao?.multaPorcentagem ?? 0}
              onBlur={(event) => onBlur(event)}
            />
          </Grid>
        </Grid>
        <Grid container spacing={2} className={"config-card"}>
          <Grid item xs={9}>
            FIN004 - Carência (em dias) para contabilização de juros/multa.
          </Grid>
          <Grid item xs={3}>
            <TextField
              id="carencia"
              name="carencia"
              label="Carência"
              type="number"
              variant="outlined"
              margin="normal"
              inputProps={{ min: 0 }}
              value={configuracao?.carencia ?? 0}
              onChange={onChange}
            />
          </Grid>
        </Grid>
        <Grid container spacing={2} className={"config-card"}>
          <Grid item xs={9}>
            FIN005 - Zerar saldo anterior automaticamente no caixa diário.
          </Grid>
          <Grid item xs={3}>
            <Switch
              id="zerarSaldoAnteriorCaixa"
              name="zerarSaldoAnteriorCaixa"
              checked={
                configuracao?.zerarSaldoAnteriorCaixa
                  ? configuracao?.zerarSaldoAnteriorCaixa
                  : false
              }
              onChange={onChangeSwitch}
              color="primary"
            />
          </Grid>
        </Grid>
        <Grid container spacing={2} className={"config-card"}>
          <Grid item xs={9}>
            FIN006 - Utilizar rotina de suprimento e sangria diretamente no
            Caixa.
          </Grid>
          <Grid item xs={3}>
            <Switch
              id="utilizarSuprimentoSangria"
              name="utilizarSuprimentoSangria"
              checked={
                configuracao?.utilizarSuprimentoSangria
                  ? configuracao?.utilizarSuprimentoSangria
                  : false
              }
              onChange={onChangeSwitch}
              color="primary"
            />
          </Grid>
        </Grid>
        <Grid container spacing={2} className={"config-card"}>
          <Grid item xs={9}>
            FIN007 - Plano financeiro de apropriação de “desconto” na NF de
            Saída.
          </Grid>
          <Grid item xs={3}>
            <FormControl variant="outlined" margin="normal" fullWidth>
              <Autocomplete
                id="planoFinanceiroDescontoNfSaidaId"
                name="planoFinanceiroDescontoNfSaidaId"
                options={listaPlanosFinanceirosDesconto}
                noOptionsText="Digite e pressione Enter"
                autoHighlight
                getOptionLabel={(option) =>
                  option.descricao
                    ? `${option.id}` + ` - ` + `${option.descricao}`
                    : ""
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Plano Financeiro Desconto NF Saída"
                    variant="outlined"
                    inputRef={inputPlanoFinanceiroDescontoNfSaidaRef}
                    onKeyDown={(e) =>
                      onSearchChangePlanoFinanceiro(
                        e,
                        inputPlanoFinanceiroDescontoNfSaidaRef,
                        setListaPlanosFinanceirosDesconto,
                        setLoadingDesconto,
                      )
                    }
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          <Fragment>
                            {loadingDesconto ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </Fragment>
                        </>
                      ),
                    }}
                  />
                )}
                onChange={(e, value) =>
                  onChangeAutocomplete(
                    "planoFinanceiroDescontoNfSaidaId",
                    value,
                  )
                }
                value={value.autoComplete(
                  listaPlanosFinanceirosDesconto,
                  configuracao?.planoFinanceiroDescontoNfSaidaId,
                )}
              />
            </FormControl>
          </Grid>
        </Grid>
        <Grid container spacing={2} className={"config-card"}>
          <Grid item xs={9}>
            FIN008 - Plano financeiro de apropriação de “Juros Recebidos” no
            Contas a Receber.
          </Grid>
          <Grid item xs={3}>
            <FormControl variant="outlined" margin="normal" fullWidth>
              <Autocomplete
                id="planoFinanceiroJurosRecebidosId"
                name="planoFinanceiroJurosRecebidosId"
                options={listaPlanosFinanceirosJuros}
                noOptionsText="Digite e pressione Enter"
                autoHighlight
                getOptionLabel={(option) =>
                  option.descricao
                    ? `${option.id}` + ` - ` + `${option.descricao}`
                    : ""
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Plano Financeiro Juros Recebidos"
                    variant="outlined"
                    inputRef={inputPlanoFinanceiroJurosRecebidosRef}
                    onKeyDown={(e) =>
                      onSearchChangePlanoFinanceiro(
                        e,
                        inputPlanoFinanceiroJurosRecebidosRef,
                        setListaPlanosFinanceirosJuros,
                        setLoadingJuros,
                      )
                    }
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          <Fragment>
                            {loadingJuros ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </Fragment>
                        </>
                      ),
                    }}
                  />
                )}
                onChange={(e, value) =>
                  onChangeAutocomplete("planoFinanceiroJurosRecebidosId", value)
                }
                value={value.autoComplete(
                  listaPlanosFinanceirosJuros,
                  configuracao?.planoFinanceiroJurosRecebidosId,
                )}
              />
            </FormControl>
          </Grid>
        </Grid>
        <Grid container spacing={2} className={"config-card"}>
          <Grid item xs={9}>
            FIN009 - Plano financeiro de apropriação de “Multa Recebida” no
            Contas a Receber.
          </Grid>
          <Grid item xs={3}>
            <FormControl variant="outlined" margin="normal" fullWidth>
              <Autocomplete
                id="planoFinanceiroMultaRecebidaId"
                name="planoFinanceiroMultaRecebidaId"
                options={listaPlanosFinanceirosMulta}
                noOptionsText="Digite e pressione Enter"
                autoHighlight
                getOptionLabel={(option) =>
                  option.descricao
                    ? `${option.id}` + ` - ` + `${option.descricao}`
                    : ""
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Plano Financeiro Multa Recebida"
                    variant="outlined"
                    inputRef={inputPlanoFinanceiroMultaRecebidaRef}
                    onKeyDown={(e) =>
                      onSearchChangePlanoFinanceiro(
                        e,
                        inputPlanoFinanceiroMultaRecebidaRef,
                        setListaPlanosFinanceirosMulta,
                        setLoadingMulta,
                      )
                    }
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          <Fragment>
                            {loadingMulta ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </Fragment>
                        </>
                      ),
                    }}
                  />
                )}
                onChange={(e, value) =>
                  onChangeAutocomplete("planoFinanceiroMultaRecebidaId", value)
                }
                value={value.autoComplete(
                  listaPlanosFinanceirosMulta,
                  configuracao?.planoFinanceiroMultaRecebidaId,
                )}
              />
            </FormControl>
          </Grid>
        </Grid>
        <Grid container spacing={2} className={"config-card"}>
          <Grid item xs={9}>
            FIN010 - Excluir histórico de pagamento ao realizar retirada de baixa
          </Grid>
          <Grid item xs={3}>
            <Switch
              id="excluirHistoricoPagamentos"
              name="excluirHistoricoPagamentos"
              checked={
                configuracao?.excluirHistoricoPagamentos
                  ? configuracao?.excluirHistoricoPagamentos
                  : false
              }
              onChange={onChangeSwitch}
              color="primary"
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};
export default ConfiguracaoFinanceiroTab;
